import React, { useState, useEffect } from 'react'
import { Clock, Check, X, ArrowRight } from 'lucide-react'
import { FeaturedLayout } from '../layouts'
import { useAuth } from '../../hooks/useAuth'
import {
  getAllPlanRequests,
  approvePlanChangeRequest,
  rejectPlanChangeRequest,
} from '../../lib/supabase'
import PlanBadge from '../ui/PlanBadge'
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label
} from '../ui'
import { Textarea } from '../ui/textarea'
import { cn } from '../../lib/utils'
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard'

const TABS = [
  { key: 'pending', label: 'Pendientes', statuses: ['pending'] },
  { key: 'resolved', label: 'Resueltas', statuses: ['approved', 'rejected', 'canceled'] },
  { key: 'all', label: 'Todas', statuses: null },
]

const STATUS_BADGE_CLASS = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  canceled: 'bg-muted text-muted-foreground border-border',
}

const STATUS_LABEL = {
  pending: 'Pendiente',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  canceled: 'Cancelada',
}

const formatDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

const ResolveModal = ({ isOpen, mode, request, onClose, onDone, staffId }) => {
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const guard = useUnsavedGuard({ onClose })

  useEffect(() => {
    if (isOpen) {
      setNote('')
      setError(null)
      guard.resetDirty()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (!isOpen || !request) return null

  const isApprove = mode === 'approve'

  const handleSubmit = async () => {
    setSaving(true)
    setError(null)
    const fn = isApprove ? approvePlanChangeRequest : rejectPlanChangeRequest
    const { error: e } = await fn({
      requestId: request.id,
      resolvedByStaffId: staffId,
      resolutionNote: note,
    })
    setSaving(false)
    if (e) {
      setError(e.message || 'No se pudo resolver la solicitud')
      return
    }
    guard.resetDirty()
    onDone(request.id)
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{isApprove ? 'Aprobar solicitud' : 'Rechazar solicitud'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm space-y-1">
              <div className="font-semibold">{request.org?.name}</div>
              <div className="flex items-center gap-2 text-xs">
                <PlanBadge plan={request.from_plan?.code || 'starter'} />
                <ArrowRight className="size-3 text-muted-foreground" />
                <PlanBadge plan={request.to_plan?.code} />
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label>Nota {isApprove ? '(opcional)' : '(motivo del rechazo)'}</Label>
              <Textarea
                value={note}
                onChange={(e) => { guard.markDirty(); setNote(e.target.value) }}
                rows={3}
                placeholder={isApprove
                  ? 'Ej: Aprobado. Bienvenido a Pro.'
                  : 'Ej: No cumple requisitos / pendiente de pago previo.'}
                maxLength={500}
              />
            </div>

            {isApprove && (
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                Al aprobar se cambiará automáticamente el plan de la org a <strong>{request.to_plan?.name}</strong>.
                Se mantiene el estado actual de grandfathered/sin cobro hasta que Stripe esté integrado.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => guard.onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              variant={isApprove ? 'default' : 'destructive'}
            >
              {saving ? 'Procesando...' : (isApprove ? 'Aprobar y cambiar plan' : 'Rechazar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.DiscardDialog}
    </>
  )
}

const PlatformPlanRequests = () => {
  const { staff } = useAuth()
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [resolving, setResolving] = useState(null) // { mode: 'approve'|'reject', request }
  const [activeTab, setActiveTab] = useState('pending')

  const currentTab = TABS.find(t => t.key === activeTab) || TABS[0]

  const load = async () => {
    setLoading(true)
    const { data } = await getAllPlanRequests({ statuses: currentTab.statuses, limit: 100 })
    setRequests(data || [])
    setLoading(false)
  }

  useEffect(() => { load() }, [activeTab])

  const handleResolved = () => {
    setResolving(null)
    // Reload para reflejar el nuevo status segun el tab actual
    load()
  }

  return (
    <FeaturedLayout
      title="Solicitudes de plan"
      subtitle="Aprueba para aplicar el cambio automáticamente, o rechaza con una nota."
    >
      <div className="max-w-3xl mx-auto flex flex-col gap-4">
        {/* Filtros por estado */}
        <div className="flex flex-wrap gap-1.5">
          {TABS.map((t) => (
            <Button
              key={t.key}
              variant={activeTab === t.key ? 'default' : 'outline'}
              size="sm"
              className="h-8 rounded-full px-3 text-xs font-normal"
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground text-sm py-8">Cargando...</div>
        ) : requests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Clock className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">
                {activeTab === 'pending'
                  ? 'No hay solicitudes pendientes'
                  : 'No hay solicitudes en esta vista'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => {
              const isPending = r.status === 'pending'
              return (
                <Card key={r.id}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className={cn(
                          'size-10 rounded-lg flex items-center justify-center shrink-0',
                          isPending ? 'bg-amber-100' : 'bg-muted'
                        )}>
                          <Clock className={cn(
                            'size-5',
                            isPending ? 'text-amber-700' : 'text-muted-foreground'
                          )} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold flex items-center gap-2 flex-wrap">
                            {r.org?.name}
                            <span className={cn(
                              'text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide border',
                              STATUS_BADGE_CLASS[r.status]
                            )}>
                              {STATUS_LABEL[r.status]}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Solicitado por <strong>{r.requested_by?.nombre_completo || r.requested_by?.email}</strong>
                            {' · '}{formatDate(r.requested_at)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs shrink-0">
                        <PlanBadge plan={r.from_plan?.code || 'starter'} />
                        <ArrowRight className="size-3 text-muted-foreground" />
                        <PlanBadge plan={r.to_plan?.code} />
                      </div>
                    </div>

                    {r.note && (
                      <div className="text-sm bg-muted/50 border rounded-lg p-3 italic">
                        "{r.note}"
                      </div>
                    )}

                    {!isPending && (
                      <div className="text-xs text-muted-foreground border-t pt-2 space-y-0.5">
                        <div>
                          Resuelta {formatDate(r.resolved_at)}
                          {r.resolved_by?.nombre_completo && (
                            <> por <strong>{r.resolved_by.nombre_completo}</strong></>
                          )}
                        </div>
                        {r.resolution_note && (
                          <div className="italic">Nota de resolución: "{r.resolution_note}"</div>
                        )}
                      </div>
                    )}

                    {isPending && (
                      <div className="flex justify-end gap-2 pt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setResolving({ mode: 'reject', request: r })}
                          className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
                        >
                          <X className="h-3.5 w-3.5 mr-1.5" />
                          Rechazar
                        </Button>
                        <Button size="sm" onClick={() => setResolving({ mode: 'approve', request: r })}>
                          <Check className="h-3.5 w-3.5 mr-1.5" />
                          Aprobar
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      <ResolveModal
        isOpen={!!resolving}
        mode={resolving?.mode}
        request={resolving?.request}
        staffId={staff?.id}
        onClose={() => setResolving(null)}
        onDone={handleResolved}
      />
    </FeaturedLayout>
  )
}

export default PlatformPlanRequests
